import React, { useState, useEffect } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import {
	Heading, Badge, Input, Link as ChakraLink, Box, Text, LinkBox, LinkOverlay, Flex, List, ListItem, Button, ListIcon,
} from '@chakra-ui/react';
import pangu from 'pangu';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';
import { FontAwesomeIcon as Fa } from '@fortawesome/react-fontawesome';
import { faCaretRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { NextSeo } from 'next-seo';
import Page from '../../components/Page';
import { Loading, Pagination } from '../../components/c';
import styles from '../../styles/datasets.module.css';
import { serversideInstance as axios } from '../../utils/axios';

export default function Home({ datasetsResult, similarTerms, ...props }) {
	const router = useRouter();
	const {
		s, service, theme, format, mode,
	} = props?.query || {};

	const [pageSize] = useState(50);
	const [offset, setOffset] = useState(0);
	const [keywordInput, setKeywordInput] = useState('');
	const [datasets, setDatasets] = useState([]);

	// const pathname = router.asPath;
	// const mode = pathname.split('?mode=')[1];
	const setQuery = (key, value) => {
		if (!router.isReady || router.isFallback) return;
		let { query = {} } = props;
		if (!value) {
			delete query[key];
		} else {
			query = {
				...query,
				[key]: value,
			};
		}
		setDatasets({});
		router.push({
			pathname: '/datasets/search',
			query,
		});
	};

	useEffect(() => {
		if (!router.isReady || router.isFallback) return;

		setOffset(0);
	}, [s, service, theme, format]);

	useEffect(() => {
		if (!router.isReady || router.isFallback) return;

		setQuery('offset', offset);
	}, [offset]);

	useEffect(() => {
		if (datasetsResult?.data) {
			setDatasets({
				headers: { 'x-total-count': datasetsResult.data.hits.extTotal.value },
				data: datasetsResult.data.hits.hits.filter((d) => d).map((d) => ({
					_id: d._id,
					...d,
				})),
				summary: datasetsResult.data.aggregations,
			});
			// console.log(datasetsResult.data.body.aggregations);
		} else {
			setDatasets({});
		}
		if (offset >= 0 && window) window.scrollTo(0, 0);
	}, [datasetsResult]);

	useEffect(() => {
		if (!s) return;
		setKeywordInput(s);
	}, [s]);

	dayjs.extend(relativeTime);
	dayjs.locale('zh-tw');

	return (
		<>
			<Page title={`${s || ''} 資料集 | 搜尋結果`}>
				<NextSeo
					title={`${s || ''} 資料集 | 搜尋結果`}
					description={`${s || ''} 資料集 | 搜尋結果`}
				/>
				<div className={styles.header}>
					<Input
						px={2}
						my={1}
						borderColor="#777"
						variant="flushed"
						type="search"
						placeholder="搜尋資料集..."
						value={keywordInput}
						onChange={(e) => { setKeywordInput(e.target.value); }}
						onKeyPress={(e) => {
							if (e.key === 'Enter') {
								setQuery('s', e.target.value);
							}
						}}
					/>
					<div className="d-flex">
						{ theme ? (
							<Badge bgColor="#797979" color="#fff" px={2} py={1} pt={2} mt={2} mr={2}>
								主題分類：
								{theme}
								<ChakraLink as="button" color="#333" ml={2} onClick={() => setQuery('theme', '')}>
									<Fa icon={faTimes} />
								</ChakraLink>
							</Badge>
						) : null}
						{ service ? (
							<Badge bgColor="#797979" color="#fff" px={2} py={1} pt={2} mt={2} mr={2}>
								服務分類：
								{service}
								<ChakraLink as="button" color="#333" ml={2} onClick={() => setQuery('service', '')}>
									<Fa icon={faTimes} />
								</ChakraLink>
							</Badge>
						) : null}
						{ format ? (
							<Badge bgColor="#797979" color="#fff" px={2} py={1} pt={2} mt={2} mr={2}>
								格式：
								{format}
								<ChakraLink as="button" color="#333" ml={2} onClick={() => setQuery('format', '')}>
									<Fa icon={faTimes} />
								</ChakraLink>
							</Badge>
						) : null}
					</div>
					<div className="d-flex justify-content-between">
						{(similarTerms && similarTerms.filter((t) => t.dist >= 0.65).length) ? (
							<div className="my-2">
								<span className="text-muted">你可能也想搜尋：</span>
								{similarTerms.filter((t) => t.dist >= 0.65).map((t) => (
									<Button
										ourline
										size="sm"
										color="info"
										className={`p-1 m-1 rounded bg-info text-white ${styles.cursorPointer}`}
										onClick={() => { setQuery('s', t.word); }}
									>
										{t.word}
									</Button>
								))}
							</div>
						) : null}
					</div>
				</div>
				<div className="mt-4">
					{ mode === 'latestDatasets' ? (
						<Heading as="h1" fontWeight={600} fontSize="2xl">最新上架資料集</Heading>
					) : null}
					{ mode === 'lastModified' ? (
						<Heading as="h1" fontWeight={600} fontSize="2xl">最新異動資料集</Heading>
					) : null}
					{ mode !== 'latestDatasets' && mode !== 'lastModified' ? (
						<Heading as="h1" fontWeight={600} fontSize="2xl">
							{s}
							搜尋結果
						</Heading>
					) : null}
				</div>
				<Flex display={['block', 'flex']}>
					<Box w={['100%', '75%']} px={[0, 2]}>
						{datasets && datasets.data ? (
							<>
								{datasets.data.map((x) => (
									<LinkBox p={5} my={4} bgColor="#fafafa" rounded="md" shadow="sm" position="relative" _hover={{ bgColor: '#f7f7f7' }}>
										<Text fontSize="xs" fontWeight={600}>{x.government.name}</Text>
										<Text fontSize="sm" position="absolute" top="1rem" right="1rem" color="#8c8c8c">
											{ mode === 'latestDatasets' ? (
												dayjs(x.createdAt).format('YYYY-MM-DD')
											) : null}
											{ mode === 'lastModified' ? (
												`${dayjs(x.lastModified).fromNow()}變更`
											) : null}
										</Text>
										<NextLink href={`/dataset/${x.nanoId}`} passHref>
											<LinkOverlay
												_hover={{
													color: 'inherit',
													textDecoration: 'none',
												}}
											>
												<Heading as="h3" fontSize="md" fontWeight={600}>
													{pangu.spacing(x.name || '')}
												</Heading>
											</LinkOverlay>
										</NextLink>
										<Box as="p" color="#484848" my={1}>
											{pangu.spacing(x?.desc?.slice(0, 100) || '')}
											{x?.desc?.length > 100 ? '...' : ''}
										</Box>
										<Box>
											{x.resources ? (
												Array.from(new Set(x.resources.map((r) => r.format))).map((f) => (
													<Badge bgColor="lightblue" color="#000" mr={1} key={`${x.datasetId}-${f}`}>{f.toUpperCase()}</Badge>
												))) : null}
										</Box>
									</LinkBox>
								))}
								<Box my={3} mb={[6, 0]} className="pagination">
									<Pagination
										size="sm"
										totalItems={datasets?.headers['x-total-count'] || 0}
										pageSize={pageSize}
										onSelect={(cp) => setOffset((cp - 1) * pageSize)}
										defaultActivePage={Math.floor(offset / pageSize) + 1}
									/>
								</Box>
							</>
						) : <Loading />}
					</Box>
					<Box w={['100%', '25%']} px={[0, 2]} mt={4}>
						{datasets?.summary && (
							<>
								<Box>
									<Heading as="h3" fontSize="md" fontWeight={600}>主題分類</Heading>
									<List ml={2} pt={2}>
										{datasets?.summary.themes.buckets.map((g) => (
											<ListItem key={`sidebar-${g.key}`}>
												<ListIcon as={Fa} icon={faCaretRight} />
												<ChakraLink my={0.5} as="button" onClick={() => setQuery('theme', g.key)}>
													{g.key || <span className="text-muted">(空白)</span>}
													<Badge ml={2} colorScheme="green" variant="outline">{g.doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Badge>
												</ChakraLink>
											</ListItem>
										))}
										{datasets?.summary.themes.sum_other_doc_count ? (
											<ListItem>
												其他
												<Badge ml={2} colorScheme="green" variant="outline">
													{datasets?.summary.themes.sum_other_doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
												</Badge>
											</ListItem>
										) : null}
									</List>
								</Box>
								<Box mt={5}>
									<Heading as="h3" fontSize="md" fontWeight={600}>服務分類</Heading>
									<List ml={2} pt={2}>
										{datasets?.summary.services.buckets.map((g) => (
											<ListItem key={`sidebar-${g.key}`}>
												<ListIcon as={Fa} icon={faCaretRight} />
												<ChakraLink my={0.5} as="button" onClick={() => setQuery('service', g.key)}>
													{g.key || <span className="text-muted">(空白)</span>}
													<Badge ml={2} colorScheme="green" variant="outline">{g.doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Badge>
												</ChakraLink>
											</ListItem>
										))}
										{datasets?.summary.services.sum_other_doc_count ? (
											<ListItem>
												其他
												<Badge ml={2} colorScheme="green" variant="outline">
													{datasets?.summary.services.sum_other_doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
												</Badge>
											</ListItem>
										) : null}
									</List>
								</Box>
								<Box mt={5}>
									<Heading as="h3" fontSize="md" fontWeight={600}>資料格式</Heading>
									<List ml={2} pt={2}>
										{datasets?.summary.formats.buckets.map((g) => (
											<ListItem key={`sidebar-${g.key}`}>
												<ListIcon as={Fa} icon={faCaretRight} />
												<ChakraLink my={0.5} as="button" onClick={() => setQuery('format', g.key)}>
													{g.key || <span className="text-muted">(空白)</span>}
													<Badge ml={2} colorScheme="green" variant="outline">{g.doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Badge>
												</ChakraLink>
											</ListItem>
										))}
										{datasets?.summary.formats.sum_other_doc_count ? (
											<ListItem>
												其他
												<Badge ml={2} colorScheme="green" variant="outline">
													{datasets?.summary.formats.sum_other_doc_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
												</Badge>
											</ListItem>
										) : null}
									</List>
								</Box>
								{/* <Card className="mt-3 rounded shadow-lg">
									<CardHeader className="font-weight-bold">
										主題分類
									</CardHeader>
									<CardBody>
										<ul className={styles.list}>
											{datasets?.summary.themes.buckets.map((g) => (
												<li key={`sidebar-${g.key}`}>
													<Button color="link" className="m-0 p-0" onClick={() => setQuery('theme', g.key)}>
														{g.key || <span className="text-muted">(空白)</span>}
														<Badge ml={2}>{g.doc_count}</Badge>
													</Button>
												</li>
											))}
											{datasets?.summary.themes.sum_other_doc_count ? (
												<li>
													其他
													<Badge ml={2}>{datasets?.summary.formats.sum_other_doc_count}</Badge>
												</li>
											) : null}
										</ul>
									</CardBody>
								</Card>
								<Card className="mt-3 rounded shadow-lg">
									<CardHeader className="font-weight-bold">
										服務分類
									</CardHeader>
									<CardBody>
										<ul className={styles.list}>
											{datasets?.summary.services.buckets.map((g) => (
												<li key={`sidebar-${g.key}`}>
													<Button color="link" className="m-0 p-0" onClick={() => setQuery('service', g.key)}>
														{g.key || <span className="text-muted">(空白)</span>}
														<Badge ml={2}>{g.doc_count}</Badge>
													</Button>
												</li>
											))}
											{datasets?.summary.services.sum_other_doc_count ? (
												<li>
													其他
													<Badge ml={2}>{datasets?.summary.formats.sum_other_doc_count}</Badge>
												</li>
											) : null}
										</ul>
									</CardBody>
								</Card>
								<Card className="mt-3 rounded shadow-lg">
									<CardHeader className="font-weight-bold">
										資料格式
									</CardHeader>
									<CardBody>
										<ul className={styles.list}>
											{datasets?.summary.formats.buckets.map((g) => (
												<li key={`sidebar-${g.key}`}>
													<Button color="link" className="m-0 p-0" onClick={() => setQuery('format', g.key)}>
														{g.key || <span className="text-muted">(空白)</span>}
														<Badge ml={2}>{g.doc_count}</Badge>
													</Button>
												</li>
											))}
											{datasets?.summary.formats.sum_other_doc_count ? (
												<li>
													其他
													<Badge ml={2}>{datasets?.summary.formats.sum_other_doc_count}</Badge>
												</li>
											) : null}
										</ul>
									</CardBody>
								</Card> */}
							</>
						)}
					</Box>
				</Flex>
			</Page>
		</>
	);
}
/*
const getQuery = (params) => {
	const paths = params?.path;
	if (Array.isArray(paths)) {
		const q = paths.find((p) => p.startsWith('query.'));
		return !q ? q : JSON.parse(decodeURIComponent(q.replace(/^query\./, '')));
	}
	return undefined;
};
*/

export const getServerSideProps = async ({ query }) => {
	// const query = getQuery(params) || {};
	const {
		s, pageSize = 50, offset = 0, service = '', theme = '', format = '', mode = '',
	} = query;
	let { sortKey = '' } = query;

	// const pathname = router.asPath;
	// const mode = pathname.split('?mode=')[1];
	if (mode === 'latestDatasets') {
		sortKey = 'createdAt';
	}
	if (mode === 'lastModified') {
		sortKey = 'lastModified';
	}

	const { data: datasetsResult } = await axios
		.get(encodeURI(`/api/v1/datasets_beta?q=${s ?? ''}&limit=${pageSize}&offset=${offset}&sort=${sortKey}&srv=${service ?? ''}&thm=${theme ?? ''}&fmt=${format ?? ''}`));
	const { data: similarTerms } = await axios.get(encodeURI(`/api/v1/similar/${s}?unique=true`));

	return {
		props: {
			query: query ?? undefined,
			datasetsResult: { data: datasetsResult },
			similarTerms,
			mode,
		},
		// revalidate: 60,
	};
};

// export const getStaticPaths = async () => {
// const { data: terms } = await axios.get('/api/v1/terms');

// const paths = terms.slice(0, 50).map((t) => ({ params: { path: [`search?s=${t[0]}`] } }));

// return {
// paths,
// fallback: true,
// };
// ;
